import React, {useEffect, useState} from "react";
import AddressSearch from "./AddressSearch";
import {FileUp, Info, Plus, Trash2, XCircle} from "lucide-react";
import * as UrlConstants from "../../utils/UrlConstants";
import * as Constants from "../../utils/Constants";
import {
    generateRandomNumber,
    getAuthToken,
    getClientIdOfUser,
    getLoggedInUserId,
    showToast,
} from "../../utils/utility";
import {Tooltip} from "react-tooltip";
import "../../styles/components/tasks.css";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import TextField from "../../components/ui/TextField";
import Select from "../../components/ui/Select";

const CreateTask = ({ taskToCopy, setTaskToCopy }) => {
  const [formData, setFormData] = useState({
    clientId: "",
    userAssignedTo: "",
    deviceId: "",
    title: "",
    description: "",
    taskStatus: "ASSIGNED",
    startTime: "",
    endTime: "",
    assignedEntity: "",
    customerList: [],
  });

  const [userList, setUserList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);

  useEffect(() => {
    fetchUserList();
    fetchDeviceList();

    if (taskToCopy && taskToCopy.customerList) {
      setFormData({
        ...formData,
        userAssignedTo: "",
        deviceId: "",
        taskStatus: "ASSIGNED",
        title: taskToCopy.title,
        description: taskToCopy.description,
        assignedEntity: taskToCopy.assignedEntity,
        customerList: [...taskToCopy.customerList],
      });
    } else {
      resetForm();
    }
  }, [taskToCopy]);

  const resetForm = () => {
    setFormData({
        clientId: "", userAssignedTo: "", deviceId: "", title: "", description: "",
        taskStatus: "ASSIGNED", startTime: "", endTime: "", assignedEntity: "", customerList: [],
    });
  };

  const fetchUserList = () => {
    const endPoint = `${Constants.API_V1}/${getClientIdOfUser()}/users/${UrlConstants.GET_ALL_ACTIVE_EMPLOYEES}`;
    fetch(`${Constants.BASE_URL}${endPoint}`, {
      method: "GET",
      headers: { Authorization: `${Constants.BEARER} ${getAuthToken()}` },
    })
      .then((response) => response.json())
      .then((data) => setUserList(data.responseData))
      .catch((error) => console.error("Error fetching user list:", error));
  };

  const fetchDeviceList = () => {
    const endPoint = `${Constants.API_V1}/${getClientIdOfUser()}/assets/${UrlConstants.GET_ALL_AVAILABLE_ASSETS}`;
    fetch(`${Constants.BASE_URL}${endPoint}`, {
      method: "GET",
      headers: { Authorization: `${Constants.BEARER} ${getAuthToken()}` },
    })
      .then((response) => response.json())
      .then((data) => setDeviceList(data.responseData))
      .catch((error) => console.error("Error fetching device list:", error));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handlePlaceSelected = (latitude, longitude, address, index) => {
    const newCustomerList = [...formData.customerList];
    newCustomerList[index] = {
      ...newCustomerList[index],
      address,
      latitude,
      longitude,
    };
    setFormData({ ...formData, customerList: newCustomerList });
  };

  const handleAddCustomer = () => {
    if (formData.customerList.length >= 10) {
      showToast("Maximum 10 service locations allowed!", { type: "error" });
      return;
    }
    const newCustomerList = [
      ...formData.customerList,
      { address: "", latitude: null, longitude: null, customerDetail: "", phoneNumber: "", emailId: "" },
    ];
    setFormData({ ...formData, customerList: newCustomerList });
  };

  const handleRemoveCustomer = (index) => {
    const newCustomerList = [...formData.customerList];
    newCustomerList.splice(index, 1);
    setFormData({ ...formData, customerList: newCustomerList });
  };

  const handleCustomerInputChange = (index, field, value) => {
    const newCustomerList = [...formData.customerList];
    newCustomerList[index][field] = value;
    setFormData({ ...formData, customerList: newCustomerList });
  };

  const handleCreateTask = () => {
    if (!formData.deviceId || !formData.userAssignedTo || !formData.title) {
      showToast("Please fill in all required fields (User, Device, Title).", { type: "error" });
      return;
    }

    if (formData.customerList.length === 0) {
      showToast("Please add at least one customer detail.", { type: "error" });
      return;
    }
    
    // Simple validation loop
    for(let i=0; i<formData.customerList.length; i++) {
        let c = formData.customerList[i];
        if(!c.latitude || !c.longitude || !c.customerDetail || !c.phoneNumber) {
            showToast(`Please fill all details for Service Location ${i+1}`, {type: 'error'});
            return;
        }
    }

    const requestBody = {
      requestBody: { ...formData },
      requestId: generateRandomNumber().toString(),
      clientId: getClientIdOfUser(),
      userId: getLoggedInUserId(),
    };

    const endPoint = `${Constants.API_V1}/${getClientIdOfUser()}/tasks`;
    fetch(`${Constants.BASE_URL}${endPoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `${Constants.BEARER} ${getAuthToken()}`,
      },
      body: JSON.stringify(requestBody),
    })
      .then((response) => {
          if(response.ok) return response.json();
          throw new Error('Failed');
      })
      .then((data) => {
        showToast("Task created successfully.", { type: "success" });
        resetForm();
        setTaskToCopy(null);
        fetchDeviceList(); 
      })
      .catch((error) => {
        showToast("Failed to create task.", { type: "error" });
      });
  };

  return (
    <div className="max-w-[1100px] mx-auto space-y-5 pb-8">
      <Card>
        <div className="p-6 border-b border-ink-100 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-ink-900">Task details</h2>
            <p className="text-sm text-ink-500 mt-1">
              Who's doing the task, with which device, and what they're doing.
            </p>
          </div>
          {taskToCopy && (
            <button
              type="button"
              onClick={() => setTaskToCopy(null)}
              className="inline-flex items-center gap-2 text-xs font-medium text-danger-700 hover:text-danger-800 hover:underline"
            >
              <Trash2 className="h-3.5 w-3.5" /> Clear copy
            </button>
          )}
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="space-y-1">
            <label className="block text-sm font-medium text-ink-700">
              Assign user <span className="text-danger-600">*</span>
            </label>
            <Select name="userAssignedTo" value={formData.userAssignedTo} onChange={handleInputChange}>
              <option value="">Select user…</option>
              {userList.map((user) => (
                <option key={user.userId} value={user.userId}>{user.fullName}</option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-ink-700">
              Assign device <span className="text-danger-600">*</span>
            </label>
            <Select name="deviceId" value={formData.deviceId} onChange={handleInputChange}>
              <option value="">Select device…</option>
              {deviceList.map((device) => (
                <option key={device.assetUnitId} value={device.assetUnitId}>{device.name} ({device.assetUnitId})</option>
              ))}
            </Select>
          </div>
          <TextField label="Task title" required name="title" value={formData.title} onChange={handleInputChange} placeholder="e.g. Inspection of warehouse" />
          <TextField label="Assigned entity" name="assignedEntity" value={formData.assignedEntity} onChange={handleInputChange} placeholder="e.g. Truck-01" />
          <div className="sm:col-span-2">
            <TextField label="Description" name="description" value={formData.description} onChange={handleInputChange} placeholder="Optional details…" />
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-6 border-b border-ink-100 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-base font-semibold text-ink-900">Service locations</h2>
            <p className="text-sm text-ink-500 mt-1">
              Add up to 10 stops. Each needs an address, customer name and phone.
            </p>
          </div>
          <Button variant="secondary" size="sm" icon={Plus} onClick={handleAddCustomer}>
            Add location
          </Button>
        </div>
        <div className="p-6 space-y-4">
          {formData.customerList.length === 0 && (
            <div className="text-center py-10 px-4 text-sm text-ink-500 border border-dashed border-ink-200 rounded-lg">
              No locations yet. Click <span className="font-medium text-ink-700">Add location</span> to add the first stop.
            </div>
          )}
          {formData.customerList.map((customer, index) => (
            <div key={index} className="rounded-xl border border-ink-200 bg-surface-subtle">
              <div className="px-4 py-2.5 flex items-center justify-between border-b border-ink-100">
                <span className="text-sm font-semibold text-ink-700">
                  Location <span className="font-mono">#{index + 1}</span>
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveCustomer(index)}
                  aria-label="Remove location"
                  className="inline-flex items-center justify-center h-7 w-7 rounded-md text-danger-600 hover:bg-danger-50"
                >
                  <XCircle className="h-4 w-4" />
                </button>
              </div>
              <div className="p-4 space-y-4 legacy-in-slideover">
                <AddressSearch
                  onPlaceSelected={(lat, lng, address) => handlePlaceSelected(lat, lng, address, index)}
                  latitude={customer.latitude}
                  longitude={customer.longitude}
                  address={customer.address}
                />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <TextField label="Customer name" required value={customer.customerDetail || ""} onChange={(e) => handleCustomerInputChange(index, "customerDetail", e.target.value)} placeholder="Acme Logistics" />
                  <TextField label="Phone" required value={customer.phoneNumber || ""} onChange={(e) => handleCustomerInputChange(index, "phoneNumber", e.target.value)} placeholder="+91-…" />
                  <TextField label="Email" value={customer.emailId || ""} onChange={(e) => handleCustomerInputChange(index, "emailId", e.target.value)} placeholder="optional@example.com" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <span className="inline-flex items-center gap-1.5 text-xs text-ink-500">
          <Info className="h-3.5 w-3.5" /> Fields marked with * are mandatory.
        </span>
        <Button variant="primary" icon={FileUp} onClick={handleCreateTask}>
          Create task
        </Button>
      </div>

      <Tooltip id="my-tooltip" />
    </div>
  );
};

export default CreateTask;